import React, { useState } from 'react';
import { BaseCRUDPage } from '../common';
import { Input, RequiredIndicator, Column, FieldError } from '../../components';
import { shippingLineService } from '../../services';
import { ShippingLine, ShippingLineCreate, ShippingLineUpdate } from '../../types';

interface ShippingLineFormData {
  code: string;
  name: string;
}

const ShippingLinesPage: React.FC = () => {
  const [formData, setFormData] = useState<ShippingLineFormData>({
    code: '',
    name: ''
  });

  // Define table columns
  const columns: Column[] = [
    { key: 'code', label: 'Code', sortable: true, searchable: true },
    { key: 'name', label: 'Name', sortable: true, searchable: true }
  ];

  // Form fields component with error support
  const formFields = (fieldErrors: Record<string, string>) => (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Code <RequiredIndicator />
        </label>
        <Input
          type="text"
          value={formData.code}
          onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
          required
          placeholder="Enter shipping line code"
        />
        <FieldError error={fieldErrors.code} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name <RequiredIndicator />
        </label>
        <Input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
          placeholder="Enter shipping line name"
        />
        <FieldError error={fieldErrors.name} />
      </div>
    </div>
  );

  // Modal icon
  const modalIcon = (
    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    </div>
  );

  return (
    <BaseCRUDPage<ShippingLine, ShippingLineCreate, ShippingLineUpdate>
      title="Shipping Line Management"
      entityName="shipping line"
      columns={columns}
      service={shippingLineService}
      formFields={formFields}
      formData={formData}
      setFormData={setFormData}
      requiredFields={['code', 'name']}
      getInitialFormData={() => ({ code: '', name: '' })}
      toCreateData={(formData) => ({
        code: formData.code,
        name: formData.name
      })}
      toUpdateData={(formData, original) => {
        const updateData: ShippingLineUpdate = {};
        if (formData.code !== original.code) updateData.code = formData.code;
        if (formData.name !== original.name) updateData.name = formData.name;
        return updateData;
      }}
      toFormData={(shippingLine) => ({
        code: shippingLine.code,
        name: shippingLine.name
      })}
      modalIcon={modalIcon}
      modalTitle={(isEdit) => ({
        title: isEdit ? 'Edit Shipping Line' : 'Create New Shipping Line',
        subtitle: isEdit ? 'Update shipping line information' : 'Add a new shipping line to the system'
      })}
      customDeleteConfirm={(shippingLine) => `Are you sure you want to delete shipping line "${shippingLine.name}"?`}
      defaultSearchColumn="code"
    />
  );
};

export default ShippingLinesPage;
